import {enableProdMode, importProvidersFrom, LOCALE_ID} from '@angular/core';
import {environment} from '@environment/environment';
import {AppComponent} from 'src/app/app.component';
import {StoreDevtoolsModule} from '@ngrx/store-devtools';
import {CustomSerializer} from '@root/serializer/custom-route-serializer';
import {EntiteEffects} from '@features/referentiel/effects/entite.effects';
import {DispositifEffects} from '@features/referentiel/effects/dispositif.effects';
import {EffectsModule} from '@ngrx/effects';
import {routerReducer, StoreRouterConnectingModule} from '@ngrx/router-store';
import {referentielFeatureKey, referentielReducers,} from '@features/referentiel/features/main.features';
import {StoreModule} from '@ngrx/store';
import { TranslateLoader, provideTranslateService, TranslatePipe } from '@ngx-translate/core';
import {BrowserAnimationsModule, provideAnimations} from '@angular/platform-browser/animations';
import {bootstrapApplication, BrowserModule} from '@angular/platform-browser';
import {CustomTranslateLoader} from '@shared/services/translation/custom-translate-loader.service';
import {HttpClient, provideHttpClient, withInterceptors, withInterceptorsFromDi} from '@angular/common/http';
import {
  createInterceptorCondition,
  INCLUDE_BEARER_TOKEN_INTERCEPTOR_CONFIG,
  IncludeBearerTokenCondition,
  includeBearerTokenInterceptor,
  provideKeycloak
} from 'keycloak-angular';
import {SessionStorageCacheService} from '@shared/services/cache/sessionstorage-cache.service';
import {DialogService} from 'primeng/dynamicdialog';
import {registerLocaleData} from '@angular/common';
import localeFr from '@angular/common/locales/fr';
import {KcProfileAdapter} from '@auth/api-mappers/kcProfile.mapper';
import {KcRolesAdapter} from '@auth/api-mappers/kcRoles.mapper';
import {AuthEffects, authFeature} from '@auth/store';
import {provideRouter, RouteReuseStrategy} from '@angular/router';
import {AppRoutes} from 'src/app/app-routing';
import {SalarieEffects} from '@features/referentiel/effects/salarie.effects';
import {
  SelectionDispositifEffect
} from '@features/demande-financement/dispositifs/store/effects/selection-dispositif.effect';
import {DispositifDataService} from '@features/demande-financement/dispositifs/service';
import {CodeNsfEffects} from '@features/referentiel/effects/code-nsf.effects';
import {errorInterceptor} from '@shared/services/errors/error.interceptor';
import {MessageService} from 'primeng/api';
import {ActionPortalEffects} from '@features/referentiel/effects/action-portal.effects';
import {PicklistEffects} from '@features/referentiel/effects/picklist.effects';
import {SmicEffects} from '@features/referentiel/effects/smic.effects';
import {
  SelectionPaymentDispositifEffect
} from '@features/demande-remboursement/dispositifs/store/effects/selection-dispositif.effect';
import {provideMatomo, withRouter} from 'ngx-matomo-client';
import {ParameterEffects} from '@features/referentiel/effects/parameter.effects';
import * as maintenanceEffects from '@features/maintenance/store/maintenance.effects';
import {maintenanceFeature} from '@features/maintenance/store/maintenance.reducers';
import {ConditionalReuseStrategy} from '@shared/router/conditional-reuse-strategy';
import {ConfigurableMessageEffects} from '@features/referentiel/effects/configurable-message.effects';
import {PortalStatusEffects} from '@features/referentiel/effects/portal-status.effects';
import {provideAnimationsAsync} from '@angular/platform-browser/animations/async';
import {providePrimeNG} from 'primeng/config';
import OpcoepPreset from './opcoep-preset';
import {provideHighcharts} from 'highcharts-angular';

registerLocaleData(localeFr, 'fr');


const apiCondition = createInterceptorCondition<IncludeBearerTokenCondition>({
  // Correspond à toute URL contenant "/api/" ou "/apis/" (insensible à la casse).
  // Cela garantit que le token Bearer Keycloak est ajouté à tous les appels backend,
  // qu'ils ciblent la passerelle (https://dev-api.opcoep.fr/api/...)
  // ou un serveur de développement local (http://localhost:8081/api/...).
  urlPattern: /\/apis?\//i
});

export function HttpLoaderFactory(http: HttpClient, cacheService: SessionStorageCacheService) {
  return new CustomTranslateLoader(http, cacheService);
}

if (environment.production) {
  enableProdMode();
}

bootstrapApplication(AppComponent, {
  providers: [
    provideRouter(AppRoutes),
    provideHighcharts({
      modules: () => [
        import('highcharts/esm/modules/variable-pie')
      ]
    }),
    { provide: RouteReuseStrategy, useClass: ConditionalReuseStrategy },
    provideKeycloak(
      {
        config:  environment.keycloakConfig,
        initOptions: {
          onLoad: 'check-sso',
          enableLogging: true,
        }
      }
    ),
    provideTranslateService({
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient, SessionStorageCacheService],
      },
    }),
    importProvidersFrom(
      BrowserAnimationsModule,
      BrowserModule,
      StoreModule.forRoot({
        [referentielFeatureKey]: referentielReducers,
        router: routerReducer,
      }),
      StoreModule.forFeature(authFeature),
      StoreModule.forFeature(maintenanceFeature),

      EffectsModule.forRoot([
        DispositifEffects,
        EntiteEffects,
        SalarieEffects,
        SelectionDispositifEffect,
        SelectionPaymentDispositifEffect,
        CodeNsfEffects,
        PicklistEffects,
        SmicEffects,
        ActionPortalEffects,
        ParameterEffects,
        maintenanceEffects,
        ConfigurableMessageEffects,
        PortalStatusEffects
      ]),
      EffectsModule.forFeature(AuthEffects),
      StoreRouterConnectingModule.forRoot({
        serializer: CustomSerializer,
      }),
      StoreDevtoolsModule.instrument({
        name: 'Mes services en ligne OF',
        maxAge: 100,
        logOnly: environment.production,
      }),
    ),
    provideAnimationsAsync(),
    providePrimeNG(
      {
        theme: {
          preset: OpcoepPreset,
          options: {
            cssLayer: {
              name: 'primeng',
              order: 'fonts , primeng, primeicons, primeflex, bootstrap, style, app'
            },
            darkModeSelector: false
          }
        }
      }
    ),
    KcProfileAdapter,
    KcRolesAdapter,
    DialogService,
    DispositifDataService,
    SessionStorageCacheService, // Ensure this service is provided
    { provide: LOCALE_ID, useValue: 'fr-FR' },
    MessageService,
    provideAnimations(),
    provideHttpClient(
        withInterceptorsFromDi(),
        withInterceptors([includeBearerTokenInterceptor, errorInterceptor])
    ),
    {
      provide: INCLUDE_BEARER_TOKEN_INTERCEPTOR_CONFIG,
      useValue: [apiCondition]
    },
    BrowserAnimationsModule,
    environment.matomositeId ?
      provideMatomo(
        {
          siteId: environment.matomositeId,
          trackerUrl: 'https://analytics.opcoep.fr/matomo.php',
          scriptUrl: 'https://analytics.opcoep.fr/matomo.js'
        },
        withRouter(),
      ) : [],
  ],
}).catch((err) => console.error(err));
